// Interfaz para mapeo de catálogos
interface CatalogMapping {
  catalog: any[];             // Array del catálogo
  nameField: string;          // Campo del catálogo que contiene el nombre (ej: 'name', 'description', 'title')
  idField: string;            // Campo del catálogo que contiene el ID
  caseSensitive?: boolean;    // Si la búsqueda es sensible a mayúsculas
}

// Interfaz para la configuración de columnas
interface ColumnConfig {
  excelColumn: string;    // Nombre de la columna en Excel
  objectKey: string;      // Clave que tendrá en el objeto resultante
  required?: boolean;     // Si la columna es obligatoria
  transform?: (value: any) => any; // Función de transformación opcional
  catalogMapping?: CatalogMapping; // Mapeo a catálogo opcional
}

// Interfaz para la configuración de validación
interface ValidationConfig {
  checkExistingCodes?: boolean;    // Si debe verificar códigos existentes
  codeColumn?: string;             // Columna que contiene el código único
  itemNumberColumn?: string;       // Columna de número de partida
  validateItemNumbers?: boolean;   // Si debe validar números de partida
  contractField?: string;          // Campo del contrato a validar
}

// Interfaz para los servicios necesarios
interface Services {
  uploadFileDialog: any;
  customMessageService: any;
  dataService: any;       // Servicio genérico para CRUD
}

// Interfaz para la configuración completa
interface ExcelUploaderConfig {
  columns: ColumnConfig[];
  validation: ValidationConfig;
  services: Services;
  entityName: string;     // Nombre de la entidad (ej: 'productos', 'usuarios')
  currentList?: any[];    // Lista actual de elementos
  search?: {};
  activeContract?: any;   // Contrato activo (si aplica)
  onMissingCatalogValues?: (missingValues: MissingCatalogValue[]) => Promise<void>; // Callback para valores faltantes
}

// Interfaz para valores faltantes en catálogos
interface MissingCatalogValue {
  row: number;
  field: string;
  value: string;
  excelColumn: string;
}

class GenericExcelUploader {
  
  /**
   * Función utilitaria para normalizar texto: quita acentos, convierte a mayúsculas y hace trim
   */
  private static normalizeText(text: any): string {
    if (!text) return '';
    
    return text
      .toString()
      .trim()
      .normalize('NFD')                    // Descompone caracteres acentuados
      .replace(/[\u0300-\u036f]/g, '')     // Elimina los acentos
      .toUpperCase();                      // Convierte a mayúsculas
  }
  
  /**
   * Función principal para cargar archivos Excel de forma genérica
   */
  static async uploadExcelFile(config: ExcelUploaderConfig): Promise<any> {
    try {
      // 1. Abrir el diálogo de carga
      const xlsxData: any[] = await config.services.uploadFileDialog.open();
      console.log('=== DATOS CRUDOS DEL EXCEL ===');
      console.log('Primeras 3 filas:', xlsxData.slice(0, 3));
      
      // 2. Procesar los datos del Excel
      const { processedData, missingCatalogValues } = await this.processExcelData(xlsxData, config);
      console.log('=== DATOS PROCESADOS ===');
      console.log('Primeras 3 filas procesadas:', processedData.slice(0, 3));
      console.log('Valores faltantes:', missingCatalogValues);
      
      // 3. Manejar valores faltantes en catálogos
      if (missingCatalogValues.length > 0) {
        if (config.onMissingCatalogValues) {
          await config.onMissingCatalogValues(missingCatalogValues);
        } else {
          await this.showMissingCatalogValuesMessage(missingCatalogValues, config);
        }
        return null; // No continuar si hay valores faltantes
      }
      
      // 4. Validar los datos procesados
      const validationResult = await this.validateData(processedData, config);
      if (!validationResult.isValid) {
        return; // La validación ya mostró el error
      }
      
      // 5. Separar elementos a crear y actualizar
      const { toCreate, toUpdate } = this.separateCreateUpdate(processedData, config);
      console.log('=== SEPARACIÓN CREATE/UPDATE ===');
      console.log('A crear:', toCreate.length, toCreate.slice(0, 2));
      console.log('A actualizar:', toUpdate.length, toUpdate.slice(0, 2));
      
      // 6. Procesar creaciones
      let searchResult = null;
      if (toCreate.length > 0) {
        console.log('=== ENVIANDO A CREATE ===');
        console.log('Datos a enviar:', { [config.entityName]: toCreate.slice(0, 2), operationType: 'multiple' });
        searchResult = await this.handleCreate(toCreate, config);
      }
      
      // 7. Procesar actualizaciones
      if (toUpdate.length > 0) {
        console.log('=== ENVIANDO A UPDATE ===');
        console.log('Datos a enviar:', { [config.entityName]: toUpdate.slice(0, 2), operationType: 'multiple' });
        searchResult = await this.handleUpdate(toUpdate, config);
      }
      
      return searchResult;
      
    } catch (err) {
      console.error('=== ERROR EN UPLOAD EXCEL ===', err);
      config.services.customMessageService.close();
      await config.services.customMessageService.open({
        title: 'Error', 
        type: 'error', 
        message: `Error: ${err.message || err}`, 
        dismissBtn: true 
      });
    }
  }
  
  /**
   * Procesa los datos del Excel según la configuración de columnas
   */
  private static processExcelData(xlsxData: any[], config: ExcelUploaderConfig): {processedData: any[], missingCatalogValues: MissingCatalogValue[]} {
    const processed = [];
    const missingCatalogValues: MissingCatalogValue[] = [];
    
    for (let i = 0; i < xlsxData.length; i++) {
      const row = xlsxData[i];
      const processedRow: any = {};
      let skipRow = false;
      
      // Procesar cada columna configurada
      for (const columnConfig of config.columns) {
        let value = row[columnConfig.excelColumn];
        
        // Aplicar transformación si existe
        if (columnConfig.transform) {
          value = columnConfig.transform(value);
        } else {
          // Transformación por defecto: normalizar texto (quitar acentos, mayúsculas, trim)
          value = this.normalizeText(value);
        }
        
        // Aplicar mapeo de catálogo si está configurado
        if (columnConfig.catalogMapping && value && value.toString().trim() !== '') {
          try {
            const mappedValue = this.mapToCatalog(value, columnConfig.catalogMapping);
            
            if (mappedValue === null) {
              // Valor no encontrado en catálogo
              missingCatalogValues.push({
                row: i + 2, // +2 porque empezamos en 0 y la primera fila son headers
                field: columnConfig.objectKey,
                value: value,
                excelColumn: columnConfig.excelColumn
              });
              skipRow = true;
              break; // Salir del loop de columnas
            } else {
              value = mappedValue;
            }
          } catch (error) {
            console.error(`Error al mapear valor "${value}" en columna "${columnConfig.excelColumn}":`, error);
            missingCatalogValues.push({
              row: i + 2,
              field: columnConfig.objectKey,
              value: value,
              excelColumn: columnConfig.excelColumn
            });
            skipRow = true;
            break;
          }
        } else if (columnConfig.catalogMapping && (!value || value.toString().trim() === '')) {
          // Si hay catalogMapping pero el valor está vacío, asignar null
          value = null;
        }
        
        // Asignar al objeto resultante
        this.setObjectValue(processedRow, columnConfig.objectKey, value);
      }
      
      // Solo agregar la fila si no se debe saltar
      if (!skipRow) {
        // Agregar información adicional si es necesario
        if (config.validation.itemNumberColumn && config.activeContract) {
          const itemNumber = row[config.validation.itemNumberColumn]?.toString();
          if (itemNumber) {
            processedRow.itemNumber = processedRow.itemNumber || {};
            processedRow.itemNumber[config.activeContract.id] = itemNumber;
          }
        }
        
        processed.push(processedRow);
      }
    }
    
    return { processedData: processed, missingCatalogValues };
  }
  
  /**
   * Valida los datos procesados
   */
  private static async validateData(data: any[], config: ExcelUploaderConfig): Promise<{isValid: boolean}> {
    // Validar campos obligatorios
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      
      for (const columnConfig of config.columns) {
        if (columnConfig.required) {
          const value = this.getObjectValue(row, columnConfig.objectKey);
          if (!value) {
            await config.services.customMessageService.open({
              title: 'Cuidado',
              type: 'warning',
              message: `El campo '${columnConfig.excelColumn}' es obligatorio.<br>Revisar fila ${i + 2}`,
              dismissBtn: true
            });
            return { isValid: false };
          }
        }
      }
    }
    
    // Validar números de partida si está configurado
    if (config.validation.validateItemNumbers && config.activeContract) {
      const itemNumberList = config.activeContract.products?.map((p: any) => p.itemNumber) || [];
      
      for (const row of data) {
        const itemNumber = row.itemNumber?.[config.activeContract.id];
        if (itemNumber && !itemNumberList.includes(itemNumber)) {
          await config.services.customMessageService.open({
            title: 'Información',
            type: 'neutral',
            message: `La partida ${itemNumber} no existe en el contrato activo.<br>Revisa las partidas de tu documento`,
            dismissBtn: true
          });
          return { isValid: false };
        }
      }
    }
    
    return { isValid: true };
  }
  
  /**
   * Separa los elementos a crear y actualizar
   */
  private static separateCreateUpdate(data: any[], config: ExcelUploaderConfig): {toCreate: any[], toUpdate: any[]} {
    const toCreate = [];
    const toUpdate = [];
    
    if (!config.validation.checkExistingCodes || !config.currentList) {
      return { toCreate: data, toUpdate: [] };
    }
    
    const existingCodes = config.currentList.map(item => 
      this.getObjectValue(item, config.validation.codeColumn || 'code')
    );
    
    for (const item of data) {
      const itemCode = this.getObjectValue(item, config.validation.codeColumn || 'code');
      
      if (existingCodes.includes(itemCode)) {
        toUpdate.push(item);
      } else {
        toCreate.push(item);
      }
    }
    
    return { toCreate, toUpdate };
  }
  
  /**
   * Maneja la creación de nuevos elementos
   */
  private static async handleCreate(toCreate: any[], config: ExcelUploaderConfig): Promise<any> {
    const response = await config.services.customMessageService.open({
      title: 'Información',
      type: 'neutral',
      message: `Se van a crear ${toCreate.length} ${config.entityName} nuevos en el sistema.`,
      dismissBtn: true,
      acceptBtn: true,
      acceptBtnText: 'Continuar'
    });
    
    if (response) {
      config.services.customMessageService.open({
        title: 'Espere',
        type: 'success',
        message: `Guardando ${config.entityName}`,
        loading: true
      });
      
      const payload = {
        [config.entityName]: toCreate,
        operationType: 'multiple'
      };
      
      console.log('=== PAYLOAD ENVIADO AL SERVICIO CREATE ===');
      console.log(JSON.stringify(payload, null, 2));
      
      try {
        const apiResponse = await config.services.dataService.create(payload);
        console.log('=== RESPUESTA DEL SERVICIO CREATE ===');
        console.log(apiResponse);
        
        config.services.customMessageService.close();
        
        if (apiResponse.state === 'success') {
          await config.services.customMessageService.open({
            title: '¡Hecho!',
            type: 'success',
            message: apiResponse.message,
            dismissBtn: true
          });
          
          // Ejecutar callback de actualización
          try {
            const searchResponse = await config.services.dataService.search(config.search);
            return searchResponse.data;
          } catch (searchError) {
            console.error('Error al recargar datos:', searchError);
            
            if (searchError.status === 401) {
              await config.services.customMessageService.open({
                title: 'Sesión Expirada',
                type: 'warning',
                message: 'Los elementos se crearon correctamente, pero tu sesión ha expiró. Por favor, actualiza la página e inicia sesión nuevamente.',
                dismissBtn: true
              });
            } else {
              await config.services.customMessageService.open({
                title: 'Información',
                type: 'neutral',
                message: 'Los elementos se crearon correctamente. Por favor, actualiza la página para ver los cambios.',
                dismissBtn: true
              });
            }
            return null;
          }
        } else {
          await config.services.customMessageService.open({
            title: 'Información',
            type: 'neutral',
            message: apiResponse.message,
            dismissBtn: true
          });
          return null;
        }
      } catch (serviceError) {
        console.error('=== ERROR EN SERVICIO CREATE ===');
        console.error(serviceError);
        config.services.customMessageService.close();
        
        await config.services.customMessageService.open({
          title: 'Error',
          type: 'error',
          message: `Error al crear elementos: ${serviceError.message || serviceError}`,
          dismissBtn: true
        });
        return null;
      }
    }
    return null;
  }
  
  /**
   * Maneja la actualización de elementos existentes
   */
  private static async handleUpdate(toUpdate: any[], config: ExcelUploaderConfig): Promise<any> {
    const response = await config.services.customMessageService.open({
      title: 'Cuidado',
      type: 'warning',
      message: `Hay ${toUpdate.length} ${config.entityName} que ya existen en el sistema. Si continúa, la información actual será actualizada.`,
      dismissBtn: true,
      acceptBtn: true,
      acceptBtnText: 'Continuar'
    });
    
    if (response) {
      config.services.customMessageService.open({
        title: 'Espere',
        type: 'success',
        message: `Actualizando ${config.entityName}`,
        loading: true
      });
      
      const apiResponse = await config.services.dataService.update({
        [config.entityName]: toUpdate,
        operationType: 'multiple'
      });
      
      config.services.customMessageService.close();
      
      if (apiResponse.state === 'success') {
        await config.services.customMessageService.open({
          title: '¡Hecho!',
          type: 'success',
          message: apiResponse.message,
          duration: 2000
        });
        
        // Ejecutar callback de actualización
        try {
          const searchResponse = await config.services.dataService.search(config.search);
          return searchResponse.data;
        } catch (searchError) {
          console.error('Error al recargar datos:', searchError);
          
          if (searchError.status === 401) {
            await config.services.customMessageService.open({
              title: 'Sesión Expirada',
              type: 'warning',
              message: 'Los elementos se actualizaron correctamente, pero tu sesión ha expirado. Por favor, actualiza la página e inicia sesión nuevamente.',
              dismissBtn: true
            });
          } else {
            await config.services.customMessageService.open({
              title: 'Información',
              type: 'neutral',
              message: 'Los elementos se actualizaron correctamente. Por favor, actualiza la página para ver los cambios.',
              dismissBtn: true
            });
          }
          return null;
        }
      } else {
        await config.services.customMessageService.open({
          title: 'Información',
          type: 'neutral',
          message: apiResponse.message,
          dismissBtn: true
        });
      }
    }
  }
  
  /**
   * Mapea un valor a su ID correspondiente en el catálogo
   */
  private static mapToCatalog(value: string, mapping: CatalogMapping): any {
    if (!value || !mapping.catalog || mapping.catalog.length === 0) {
      return null;
    }
    
    // Normalizar el valor de búsqueda usando la nueva función
    const searchValue = mapping.caseSensitive ? value : this.normalizeText(value);
    
    const foundItem = mapping.catalog.find(item => {
      if (!item || item[mapping.nameField] === undefined || item[mapping.nameField] === null) {
        return false;
      }
      
      const catalogValue = mapping.caseSensitive 
        ? item[mapping.nameField].toString()
        : this.normalizeText(item[mapping.nameField]);
        
      return catalogValue === searchValue;
    });
    
    return foundItem ? foundItem[mapping.idField] : null;
  }
  
  /**
   * Muestra mensaje con valores faltantes en catálogos
   */
  private static async showMissingCatalogValuesMessage(missingValues: MissingCatalogValue[], config: ExcelUploaderConfig): Promise<void> {
    const groupedByField = missingValues.reduce((acc, item) => {
      if (!acc[item.field]) acc[item.field] = [];
      acc[item.field].push(item);
      return acc;
    }, {} as Record<string, MissingCatalogValue[]>);
    
    let message = 'Se encontraron valores que no existen en los catálogos:<br><br>';
    
    Object.keys(groupedByField).forEach(field => {
      const items = groupedByField[field];
      message += `<strong>${items[0].excelColumn}:</strong><br>`;
      items.forEach(item => {
        message += `• Fila ${item.row}: "${item.value}"<br>`;
      });
      message += '<br>';
    });
    
    message += `Se omitieron ${missingValues.length} filas. Por favor, corrige estos valores y vuelve a cargar el archivo.`;
    
    await config.services.customMessageService.open({
      title: 'Valores no encontrados en catálogos',
      type: 'warning',
      message: message,
      dismissBtn: true
    });
  }
  
  /**
   * Obtiene un valor de un objeto usando notación de puntos
   */
  private static getObjectValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }
  
  /**
   * Establece un valor en un objeto usando notación de puntos
   */
  private static setObjectValue(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    const target = keys.reduce((current, key) => {
      if (!current[key]) current[key] = {};
      return current[key];
    }, obj);
    target[lastKey] = value;
  }
}

export { GenericExcelUploader, ColumnConfig, ValidationConfig, ExcelUploaderConfig, CatalogMapping, MissingCatalogValue };